import React, { useState, useEffect } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";

const emptyForm = { name: "", description: "" };

const AssetHeadList = () => {
  const { http } = AuthUser();
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const response = await http.get("/asset_heads");
      if (response.data.success) setRows(response.data.data);
    } catch (error) {
      toast.error("Error loading asset heads");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error("Head name is required");
      return;
    }
    setIsLoading(true);
    try {
      if (editing) {
        await http.put(`/asset_heads/${editing.id}`, formData);
        toast.success("Asset head updated successfully");
      } else {
        await http.post("/asset_heads", formData);
        toast.success("Asset head created successfully");
      }
      setShowModal(false);
      setFormData(emptyForm);
      setEditing(null);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving asset head");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({ name: row.name || "", description: row.description || "" });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset head?")) return;
    try {
      await http.delete(`/asset_heads/${id}`);
      toast.success("Asset head deleted");
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting");
    }
  };

  const filtered = rows.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="container-fluid mt-4">
      <Toaster />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Asset Head</h5>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              setEditing(null);
              setFormData(emptyForm);
              setShowModal(true);
            }}
          >
            <i className="fas fa-plus-circle me-1"></i> Add Head
          </button>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Head Name</th>
                  <th>Description</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.name}</td>
                      <td>{row.description || "-"}</td>
                      <td>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn-sm btn-warning me-1"
                          onClick={() => handleEdit(row)}
                          title="Edit"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(row.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No asset heads found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editing ? "Edit Asset Head" : "Add Asset Head"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      Head Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetHeadList;
